import React, { useState, useEffect } from 'react';
import { FiArrowLeft, FiTrash2, FiSmartphone, FiMonitor, FiTablet, FiRefreshCw } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { toast } from '../utils/toast';
import Swal from 'sweetalert2';
import useNotifications from '../hooks/useNotifications';
import notificationApi from '../services/notificationApi';

const NotificationSettings = () => {
  const navigate = useNavigate();
  const {
    isSupported,
    permission,
    isEnabled,
    canRequest,
    isLoading,
    settings,
    unreadCount,
    requestPermission,
    updateSettings,
    sendTestNotification,
    disableNotifications,
    hasAnyNotificationEnabled,
    initialize
  } = useNotifications();

  const [localSettings, setLocalSettings] = useState(settings);
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [refreshingDevices, setRefreshingDevices] = useState(false);

  useEffect(() => {
    fetchDevices();
    // Initialize notifications to load settings from server
    initialize();
  }, [initialize]);

  // Update local settings when settings from hook change
  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const fetchDevices = async () => {
    try {
      setLoadingDevices(true);
      const response = await notificationApi.getDevices();
      setDevices(response.data || []);
    } catch (error) {
      console.error('Error fetching devices:', error);
      toast.error('Failed to fetch devices');
    } finally {
      setLoadingDevices(false);
    }
  };

  const handleRefreshDevices = async () => {
    setRefreshingDevices(true);
    await fetchDevices();
    setRefreshingDevices(false);
  };

  const handleDeleteDevice = async (deviceId) => {
    const result = await Swal.fire({
      title: 'Delete Device?',
      text: 'Are you sure you want to remove this device? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: 'var(--color-primary, #087F4C)',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      customClass: {
        confirmButton: 'swal2-confirm-button',
        cancelButton: 'swal2-cancel-button'
      },
      buttonsStyling: false,
      didOpen: () => {
        // Add custom styles to ensure buttons are visible
        const style = document.createElement('style');
        style.textContent = `
          .swal2-confirm-button {
            background-color: var(--color-primary, #087F4C) !important;
            color: white !important;
            border: none !important;
            padding: 10px 20px !important;
            border-radius: 6px !important;
            font-weight: 500 !important;
            margin: 0 5px !important;
            cursor: pointer !important;
          }
          .swal2-cancel-button {
            background-color: #d33 !important;
            color: white !important;
            border: none !important;
            padding: 10px 20px !important;
            border-radius: 6px !important;
            font-weight: 500 !important;
            margin: 0 5px !important;
            cursor: pointer !important;
          }
          .swal2-confirm-button:hover {
            background-color: #065a36 !important;
          }
          .swal2-cancel-button:hover {
            background-color: #b02a2a !important;
          }
        `;
        document.head.appendChild(style);
      }
    });

    if (result.isConfirmed) {
      try {
        await notificationApi.removeDevice(deviceId);
        setDevices(prev => prev.filter(device => device.id !== deviceId));
        toast.success('Device removed successfully');
      } catch (error) {
        console.error('Error deleting device:', error);
        toast.error('Failed to remove device');
      }
    }
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType?.toLowerCase()) {
      case 'mobile':
      case 'android':
      case 'ios':
        return <FiSmartphone className="w-5 h-5" />;
      case 'tablet':
        return <FiTablet className="w-5 h-5" />;
      case 'web':
      case 'desktop':
      default:
        return <FiMonitor className="w-5 h-5" />;
    }
  };

  const formatLastSeen = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleToggleNotifications = async () => {
    if (isEnabled) {
      await disableNotifications();
    } else if (canRequest) {
      await requestPermission();
    }
  };

  const handleSettingChange = (key, value) => {
    const newSettings = { ...localSettings, [key]: value };
    setLocalSettings(newSettings);
    updateSettings(newSettings);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-md mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <FiArrowLeft className="w-6 h-6 text-gray-600" />
            </button>
            <h1 className="text-xl font-semibold text-gray-900">Notification Settings</h1>
            <div className="w-10" /> {/* Spacer */}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-md mx-auto px-4 py-6 space-y-6">
        {/* Not Supported Warning */}
        {!isSupported && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Push notifications not supported
                </h3>
                <p className="mt-1 text-sm text-yellow-700">
                  Your browser doesn't support push notifications. Please use a modern browser like Chrome, Firefox, or Safari.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Main Settings Card */}
        {isSupported && (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Push Notifications</h2>
              {unreadCount > 0 && (
                <span className="bg-primary100 text-white text-xs px-2 py-1 rounded-full">
                  {unreadCount} unread
                </span>
              )}
            </div>

            {/* Main Toggle */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h4 className="text-sm font-medium text-gray-900">Enable Notifications</h4>
                <p className="text-sm text-gray-500">
                  {permission === 'granted' 
                    ? 'Receive notifications about appointments and updates'
                    : permission === 'denied'
                    ? 'Notifications are blocked. Please enable them in your browser settings.'
                    : 'Enable notifications to stay updated'
                  }
                </p>
              </div>
              <button
                onClick={handleToggleNotifications}
                disabled={isLoading || permission === 'denied'}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary100 focus:ring-offset-2 ${
                  isEnabled ? 'bg-primary100' : 'bg-gray-200'
                } ${isLoading || permission === 'denied' ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    isEnabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {/* Permission Status */}
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`h-2 w-2 rounded-full mr-2 ${
                    permission === 'granted' ? 'bg-green-400' : 
                    permission === 'denied' ? 'bg-red-400' : 'bg-yellow-400'
                  }`} />
                  <span className="text-sm text-gray-600">
                    Status: {permission === 'granted' ? 'Enabled' : permission === 'denied' ? 'Blocked' : 'Not requested'}
                  </span>
                </div>
                
                {/* Action buttons based on permission status */}
                {permission === 'default' && (
                  <button
                    onClick={requestPermission}
                    disabled={isLoading}
                    className="px-4 py-2 bg-primary100 text-white text-sm font-medium rounded-lg hover:bg-primary100/90 focus:outline-none focus:ring-2 focus:ring-primary100 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isLoading ? 'Requesting...' : 'Request Permission'}
                  </button>
                )}
                
                {permission === 'denied' && (
                  <button
                    onClick={() => {
                      // Show instructions for enabling notifications
                      toast.info(
                        <div>
                          <p className="font-medium mb-2">To enable notifications:</p>
                          <ol className="text-sm space-y-1 list-decimal list-inside">
                            <li>Click the lock/info icon in your browser's address bar</li>
                            <li>Find "Notifications" and set it to "Allow"</li>
                            <li>Refresh this page</li>
                          </ol>
                        </div>,
                        {
                          position: "top-center",
                          autoClose: 8000,
                          hideProgressBar: false,
                          closeOnClick: true,
                          pauseOnHover: true,
                          draggable: true,
                        }
                      );
                    }}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                  >
                    Enable in Browser
                  </button>
                )}
              </div>
              
              {/* Status-specific messages */}
              {permission === 'default' && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-800">
                    <span className="font-medium">Action Required:</span> Click "Request Permission" above to enable push notifications and stay updated with important information.
                  </p>
                </div>
              )}
              
              {permission === 'denied' && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-800">
                    <span className="font-medium">Notifications Blocked:</span> You've previously blocked notifications. Click "Enable in Browser" above for instructions to re-enable them.
                  </p>
                </div>
              )}
              
              {/* Note when all notifications are off */}
              {permission === 'granted' && !hasAnyNotificationEnabled && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-800">
                    <span className="font-medium">Note:</span> Turn on at least one notification type below to enable notifications.
                  </p>
                </div>
              )}
            </div>

            {/* Notification Type Settings */}
            <div className="space-y-4 mb-6">
              <h4 className="text-sm font-medium text-gray-900">Notification Types</h4>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Appointment Reminders</label>
                    <p className="text-xs text-gray-500">Notifications about upcoming appointments</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={localSettings.appointment_reminders}
                    onChange={(e) => handleSettingChange('appointment_reminders', e.target.checked)}
                    className="h-4 w-4 text-primary100 focus:ring-primary100 border-gray-300 rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Payment Receipts</label>
                    <p className="text-xs text-gray-500">Notifications for payment confirmations and receipts</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={localSettings.payment_receipts}
                    onChange={(e) => handleSettingChange('payment_receipts', e.target.checked)}
                    className="h-4 w-4 text-primary100 focus:ring-primary100 border-gray-300 rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Wallet Credits</label>
                    <p className="text-xs text-gray-500">Notifications about wallet balance and credits</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={localSettings.wallet_credits}
                    onChange={(e) => handleSettingChange('wallet_credits', e.target.checked)}
                    className="h-4 w-4 text-primary100 focus:ring-primary100 border-gray-300 rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">General Notifications</label>
                    <p className="text-xs text-gray-500">News and general app updates</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={localSettings.general_notifications}
                    onChange={(e) => handleSettingChange('general_notifications', e.target.checked)}
                    className="h-4 w-4 text-primary100 focus:ring-primary100 border-gray-300 rounded"
                  />
                </div>
              </div>
            </div>

            {/* Test Notification Button */}
            {isEnabled && (
              <div className="border-t pt-4">
                <button
                  onClick={sendTestNotification}
                  className="inline-flex items-center px-4 py-2 bg-primary100 text-white text-sm font-medium rounded-lg hover:bg-primary100/90 transition-colors"
                >
                  <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM4 19h6v-2H4v2zM4 15h8v-2H4v2zM4 11h8V9H4v2z" />
                  </svg>
                  Send Test Notification
                </button>
              </div>
            )}

            {/* Browser Instructions */}
            {permission === 'denied' && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-red-800 mb-2">
                  Notifications are blocked
                </h4>
                <p className="text-sm text-red-700 mb-2">
                  To enable notifications, please:
                </p>
                <ol className="text-sm text-red-700 list-decimal list-inside space-y-1">
                  <li>Click the lock icon in your browser's address bar</li>
                  <li>Change notifications from "Block" to "Allow"</li>
                  <li>Refresh this page</li>
                </ol>
              </div>
            )}
          </div>
        )}

        {/* Registered Devices Card */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Registered Devices</h2>
            <button
              onClick={handleRefreshDevices}
              disabled={refreshingDevices}
              className="p-2 text-gray-500 hover:text-primary100 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <FiRefreshCw className={`w-4 h-4 ${refreshingDevices ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {loadingDevices ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary100"></div>
            </div>
          ) : devices.length === 0 ? (
            <div className="text-center py-8">
              <FiMonitor className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No devices registered</p>
              <p className="text-gray-400 text-xs mt-1">Enable notifications to register this device</p>
            </div>
          ) : (
            <div className="space-y-3">
              {devices.map((device) => (
                <div
                  key={device.id}
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="text-primary100">
                      {getDeviceIcon(device.device_type)}
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">
                        {device.device_name || 'Unknown Device'}
                      </h4>
                      <div className="flex items-center space-x-2 text-xs text-gray-500">
                        <span>{device.device_type || 'Unknown'}</span>
                        <span>•</span>
                        <span>Last seen: {formatLastSeen(device.last_used_at)}</span>
                      </div>
                      {device.is_current && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1">
                          Current Device
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {!device.is_current && (
                    <button
                      onClick={() => handleDeleteDevice(device.id)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove device"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;